import { inject, Injectable } from '@angular/core';
import { INews } from '@core/interfaces/news.interface';
import { NewsService } from '@core/services/news.service';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap } from 'rxjs';

interface INotificationsState {
  error: string | null;
  languageCode: string | null;
  loading: boolean;
  news: INews[];
}

const initialState: INotificationsState = {
  error: null,
  languageCode: null,
  loading: false,
  news: [],
};

@Injectable()
export class NotificationsButtonStore extends ComponentStore<INotificationsState> {
  public readonly news$ = this.selectSignal((state: INotificationsState) => state.news);
  public readonly loading$ = this.selectSignal((state: INotificationsState) => state.loading);
  public readonly error$ = this.selectSignal((state: INotificationsState) => state.error);
  public readonly languageCode$ = this.selectSignal((state: INotificationsState) => state.languageCode);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly fetchNews = this.effect((languageCode$: Observable<string>) => {
    return languageCode$.pipe(
      // A different language means a different set of news - drop the old list right away so it
      // can't linger in the wrong language, even if this fetch fails.
      tap((languageCode) =>
        this.patchState((state) => ({
          error: null,
          languageCode,
          loading: true,
          news: state.languageCode === languageCode ? state.news : [],
        })),
      ),
      switchMap((languageCode) =>
        this.newsService.getNews(languageCode).pipe(
          tap((news: INews[]) => {
            this.patchState({ loading: false, news });
          }),
          this.handleError('Failed to load news'),
        ),
      ),
    );
  });

  private readonly newsService = inject(NewsService);

  constructor() {
    super(initialState);
  }
}
